import { apiClient } from "./apiClient";
import { localDay } from "./hiveActions";

export const hivesApi = {
  /* WHOSE DAY IT IS (#397 follow-up). season_closed_until is a DATE
     the user picked, and this app decides closed-for-season in LOCAL
     time -- so the server judging it in UTC meant the phone and the
     server disagreed all evening about whether a hive was still shut,
     and the server counted it overdue while the badge said otherwise.
     Absent, the server falls back to its own day, which is what every
     older client will keep doing. */
  list: (params) => apiClient
    .get("/hives", { params: { ...params, today: localDay() } })
    .then((r) => r.data),
  get: (hiveId) => apiClient.get(`/hives/${hiveId}`).then((r) => r.data),
  /*
   * Which hives are still inside a treatment's withdrawal period (#404).
   *
   * Its own call rather than a field on the hive list: GET /hives is the
   * hottest read in the app and this needs a per-row lookup over the
   * treatment log, while almost every account has zero hives in
   * withdrawal almost all of the time.
   *
   * Returns DATES. Whether today is past one is decided locally --
   * hiveActions.withdrawalFor -- because a DATE belongs to the user's
   * calendar rather than the server's.
   */
  withdrawal: () => apiClient.get("/hives/withdrawal").then((r) => r.data),
  getFull: (hiveId) => apiClient
    .get(`/hives/${hiveId}/full`, { params: { today: localDay() } })
    .then((r) => r.data),
  // Self-assigned reminders on a hive (#170). Ungated, unlike teamApi's
  // task endpoints, which need TeamCollaboration -- see functions/hives.py.
  createFollowUp: (hiveId, payload) => apiClient.post(`/hives/${hiveId}/follow-up`, payload).then((r) => r.data),
  listFollowUps: (hiveId) => apiClient.get(`/hives/${hiveId}/follow-ups`).then((r) => r.data),
  completeFollowUp: (hiveId, taskId) => apiClient.post(`/hives/${hiveId}/follow-up/${taskId}/complete`).then((r) => r.data),
  create: (payload) => apiClient.post("/hives", payload).then((r) => r.data),
  update: (hiveId, payload) => apiClient.put(`/hives/${hiveId}`, payload).then((r) => r.data),
  /* A BODY ON DELETE, for the archive reason (#428).
     axios sends it as `data`, and the server treats an absent body as
     "no reason given" rather than an error -- archiving has never
     required one, and a hive being archived after a colony died is not
     the moment to insist on a form. */
  remove: (hiveId, { reason, note } = {}) =>
    apiClient.delete(`/hives/${hiveId}`, {
      data: reason || note ? { reason: reason || undefined, note: note || undefined } : undefined,
    }).then((r) => r.data),
  /* The way back (#365). Archiving has never deleted anything -- it sets
     is_archived = 1 and status = 'Archived' -- so this is a status change,
     not a recovery. */
  restore: (hiveId) => apiClient.post(`/hives/${hiveId}/restore`).then((r) => r.data),
  logAction: (hiveId, payload) => apiClient.post(`/hives/${hiveId}/action`, payload).then((r) => r.data),

  // Issues are their own record now, not an action with no details (#251).
  // An issue opens, persists and closes; an action is a thing that
  // happened. openIssue writes both -- the state and the timeline entry.
  openIssue: (hiveId, payload) => apiClient.post(`/hives/${hiveId}/issues`, payload).then((r) => r.data),
  listIssues: (hiveId) => apiClient.get(`/hives/${hiveId}/issues`).then((r) => r.data),
  openIssues: () => apiClient.get("/issues").then((r) => r.data),
  /* A COLONY LOST, as a regulatory filing (#432). app.HiveLossReports has
     been read by the Loss report since V012 and written by nothing, so
     that report has always come back empty -- and an empty loss report
     is indistinguishable from a season with no losses.

     Deliberately NOT derived from the archive reason (#428): "Disease"
     does not say whether it was Varroa or Foulbrood, and whether you
     told the agency is a fact about what you did. */
  recordLoss: (hiveId, payload) => apiClient.post(`/hives/${hiveId}/loss`, payload).then((r) => r.data),
  resolveIssue: (issueId, resolution) =>
    apiClient.post(`/issues/${issueId}/resolve`, { resolution }).then((r) => r.data),
  reopenIssue: (issueId) => apiClient.post(`/issues/${issueId}/reopen`, {}).then((r) => r.data),
  // Takes the same params as list() so the map and the table can never show
  // different selections -- see hives_map in functions/hives.py.
  map: (params) => apiClient.get("/hives/map", { params }).then((r) => r.data),
  due: () => apiClient.get("/hives/due").then((r) => r.data),

  batchUpdate: (hiveIds, fields) => apiClient.post("/hives/batch-update", { hive_ids: hiveIds, fields }).then((r) => r.data),
  /* One reason for the whole selection (#428): somebody archiving eleven
     hives at once is doing it because the same thing happened to all of
     them, and asking per hive turns a batch operation back into eleven
     single ones. */
  batchArchive: (hiveIds, { reason, note } = {}) =>
    apiClient.post("/hives/batch-archive", {
      hive_ids: hiveIds, reason: reason || undefined, note: note || undefined,
    }).then((r) => r.data),
  /* The way back. Batch archive has existed since batch operations
     shipped and batch restore never did, so a mis-aimed selection could
     archive eleven hives in one press and needed eleven separate
     restores to undo -- the easy direction being the destructive one.
     No reason or note: archiving is the act that needs explaining. */
  batchRestore: (hiveIds) =>
    apiClient.post("/hives/batch-restore", { hive_ids: hiveIds }).then((r) => r.data),
  /*
   * Close hives for the season, or open them again (#389).
   *
   * ONE call for one hive or fifty -- the endpoint is a batch and this
   * keeps it that way, so a yard leaving for Florida is a single request
   * that cannot half-apply.
   *
   * `closedUntil` IS the switch: a YYYY-MM-DD string closes, null opens.
   * No separate flag, so the two can never disagree.
   */
  setSeason: (hiveIds, closedUntil) =>
    apiClient.post("/hives/season", {
      hive_ids: hiveIds,
      closed_until: closedUntil ?? null,
    }).then((r) => r.data),
  batchTreatment: (payload) => apiClient.post("/hives/batch-treatment", payload).then((r) => r.data),
  batchInspection: (payload) => apiClient.post("/hives/batch-inspection", payload).then((r) => r.data),
  batchAdvanced: (payload) => apiClient.post("/hives/batch-advanced", payload).then((r) => r.data),
  saveBatchTemplate: (payload) => apiClient.post("/hives/batch-template", payload).then((r) => r.data),
  listBatchTemplates: () => apiClient.get("/hives/batch-templates").then((r) => r.data),
  batchHistory: (params) => apiClient.get("/hives/batch-history", { params }).then((r) => r.data),

  exportCsvUrl: () => `${apiClient.defaults.baseURL}/hives/export/csv`,
  exportAdvanced: () => apiClient.get("/hives/export/advanced").then((r) => r.data),

  split: (hiveId, payload) => apiClient.post(`/hives/${hiveId}/split`, payload).then((r) => r.data),
  swarmCapture: (hiveId, payload) => apiClient.post(`/hives/${hiveId}/swarm-capture`, payload).then((r) => r.data),
  merge: (hiveId, payload) => apiClient.post(`/hives/${hiveId}/merge`, payload).then((r) => r.data),
  queenLineage: (hiveId) => apiClient.get(`/hives/${hiveId}/queen-lineage`).then((r) => r.data),
  colonyEvents: (hiveId) => apiClient.get(`/hives/${hiveId}/colony-events`).then((r) => r.data),

  /*
   * AN EXPLICIT LIMIT, because the endpoint pages now (#372).
   *
   * parse_pagination defaults to 50, so a caller that says nothing gets
   * the first 50 rows and no sign there are more -- which is how "50 of
   * 800 looked exactly like all 50" happened before, and it would be a
   * REGRESSION here rather than a pre-existing gap: this list used to
   * return everything.
   *
   * 200 matches the other logs. The response carries `total`, so the
   * screen can say when it is showing a window rather than the lot.
   */
  listPhotos: (hiveId, params = { limit: 200 }) =>
    apiClient.get(`/hives/${hiveId}/photos`, { params }).then((r) => r.data),
  uploadPhoto: (hiveId, actionId, payload) => apiClient.post(`/hives/${hiveId}/action/${actionId}/photo`, payload).then((r) => r.data),
};
